import React, { Dispatch, FC, SetStateAction, useContext, useState } from 'react';
import { Modal } from 'antd';
import styled from '@emotion/styled';

import _ from 'lodash';

import { pricingSimulatorDataContext } from 'contexts/PricingSimulatorContext';
import { pricingSimulatorUrls } from 'config/urls';

import axios from 'axios';
import FileUpload from 'components/molecules/common/Form/FileUpload';
import { UploadPollingStatus } from '../ScenarioActions';
import { BulkUploadInfo } from 'types/pricingSimulator';
import { sendNotification } from 'utils/helperMethods';

type Props = {
    open: boolean;
    onCancel: () => void;
    onSubmitFinish: (data: any) => void;
    setPollingStatus: Dispatch<SetStateAction<UploadPollingStatus | null>>;
};

const CustomModal = styled(Modal)`
    .ant-modal-body {
        padding: 0;
    }
    .ant-modal-content {
        padding: 12px 18px 24px 18px;
        border-radius: 8px !important;
    }
    .ant-modal-header {
        padding: 8px 0px;

        border-bottom: none !important;
    }
    .ant-modal-footer {
        border-top: none !important;
        padding: 16px 0 0 0;
    }
    .ant-modal-close-icon {
        position: relative;
        top: -2px;
    }
    .ant-modal-title {
        font-weight: 400 !important;
    }
`;

const BulkUploadModal: FC<Props> = ({ open, onCancel, onSubmitFinish, setPollingStatus }) => {
    const { selectedScenarioInfo, updateScenarioProperty } = useContext(pricingSimulatorDataContext);

    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);

    const handleFileChange = (file: File) => {
        if (file) {
            setFile(file);
        }
    };

    const onClose = () => {
        setFile(null);
        onCancel();
    };

    const handleUpload = async () => {
        if (selectedScenarioInfo?.scenarioId && file && selectedScenarioInfo?.projectId) {
            setUploading(true); // Start loading

            const formData = new FormData();
            formData.append('file', file);
            formData.append('scenario_id', selectedScenarioInfo?.scenarioId.toString());
            formData.append('project_id', selectedScenarioInfo?.projectId.toString());

            try {
                const res = await axios.post(`${pricingSimulatorUrls.uploadScenarioExcel}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });

                const newBulkUploadInfo: BulkUploadInfo = {
                    status: res?.data?.bulk_upload_status,
                    errorObject: {},
                };

                updateScenarioProperty('bulkUploadInfo', newBulkUploadInfo);
                setPollingStatus(res?.data?.bulk_upload_status);
                onClose();
            } catch (err: any) {
                sendNotification('error', 'Upload failed. Please try again.');
            } finally {
                setUploading(false); // Stop loading
            }
        }
    };

    return (
        <div>
            <CustomModal
                style={{ borderRadius: 8 }}
                cancelButtonProps={{ style: { borderRadius: 6 } }}
                okButtonProps={{
                    htmlType: 'submit',
                    disabled: !file,
                    loading: uploading,
                    style: { borderRadius: 6, minWidth: '140px' },
                    className: 'disabled:bg-red-500 disabled:opacity-50',
                }}
                okText={'Upload'}
                maskClosable={false}
                destroyOnClose
                centered
                onOk={handleUpload}
                open={open}
                onCancel={onClose}
                title="Bulk Update: Upload"
                width={420}
            >
                <div className="px-3 py-6 border rounded-[3px] border-backgroundGreyAlt1">
                    <span className="mb-8px">Upload</span>
                    <p className="text-xs text-fontGreyAlt mb-4">
                        Edit the downloaded Excel file without changing its structure, then upload it here to update in
                        bulk.
                        <br />
                        <br />
                        Please download the template from the previous step.
                    </p>

                    <FileUpload
                        allowedFormats={['.xlsx', '.xls']}
                        onFileUpload={handleFileChange}
                        maxFileSizeInMB={25}
                        onFileRemove={() => {
                            setFile(null);
                        }}
                    />
                </div>
            </CustomModal>
        </div>
    );
};

export default BulkUploadModal;
